import { create } from 'zustand';

/** A 412 STALE_ITEM waiting for the user's choice (reload, or overwrite their copy). */
export interface StaleRequest {
  /** What was being saved, e.g. "Dresses" — named in the dialog. */
  itemLabel: string;
  /** Re-sends the write without `If-Match`, replacing the other person's edit. */
  overwrite: () => void;
}

/** Why the whole studio is unusable (401, NOT_PLATFORM_ADMIN, INSUFFICIENT_SCOPE …). */
export interface AccessProblem {
  status: number | null;
  code: string | undefined;
  detail: string;
}

/**
 * Taxonomy Studio client state shared across the studio:
 * - "whose change is this": `knownVersion` is the `draft_version` this tab has accounted
 *   for (first sight of the draft, our own writes). A polled draft past it was edited by
 *   someone else.
 * - studio-wide error surfaces: the full-page access message, the "never imported" empty
 *   state, the publish-checks popover (opened on CHECKS_FAILED) and the STALE_ITEM dialog.
 */
interface TaxonomyStudioState {
  draftSeq: number | null;
  knownVersion: number | null;
  accessProblem: AccessProblem | null;
  notInitialized: boolean;
  checksOpen: boolean;
  stale: StaleRequest | null;
}

interface TaxonomyStudioActions {
  setKnown: (draftSeq: number, version: number) => void;
  reset: () => void;
  setAccessProblem: (p: AccessProblem | null) => void;
  setNotInitialized: (v: boolean) => void;
  setChecksOpen: (open: boolean) => void;
  setStale: (stale: StaleRequest | null) => void;
}

export const useTaxonomyStudioStore = create<TaxonomyStudioState & TaxonomyStudioActions>()(
  (set) => ({
    draftSeq: null,
    knownVersion: null,
    accessProblem: null,
    notInitialized: false,
    checksOpen: false,
    stale: null,
    setKnown: (draftSeq, version) => {
      set({ draftSeq, knownVersion: version });
    },
    reset: () => {
      set({ draftSeq: null, knownVersion: null });
    },
    setAccessProblem: (accessProblem) => {
      set({ accessProblem });
    },
    setNotInitialized: (notInitialized) => {
      set({ notInitialized });
    },
    setChecksOpen: (checksOpen) => {
      set({ checksOpen });
    },
    setStale: (stale) => {
      set({ stale });
    },
  }),
);
